import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Search, Mail, Key, Check, AlertTriangle, ChevronLeft, ChevronRight, Send, Users, RefreshCw } from 'lucide-react';

// Embeddable variant: renders the content WITHOUT the <Layout> wrapper.
// Used as a tab inside ClientsPage. Phase B2 — 2026-04-29.
export const ClientPasswordsContent = () => {
  const { api } = useAuth();
  const { t, tError } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sending, setSending] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, search, filter_sent: filter });
      const res = await api.get(`/clients/migrated?${params}`);
      setClients(res.data.clients);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, page, search, filter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const generatePin = async (clientId) => {
    setSending(p => ({ ...p, [clientId]: 'generating' }));
    try {
      const res = await api.post(`/clients/${clientId}/generate-password`);
      setMessage({ type: 'success', text: t('clientPwd.pinGenerated', { nom: res.data.client_nom, pin: res.data.pin }) });
      fetchClients();
    } catch (err) {
      setMessage({ type: 'error', text: tError(err.response?.data?.detail) || t('clientPwd.error') });
    } finally {
      setSending(p => ({ ...p, [clientId]: null }));
    }
  };

  const sendEmail = async (clientId) => {
    setSending(p => ({ ...p, [clientId]: 'sending' }));
    try {
      const res = await api.post(`/clients/${clientId}/send-password`);
      setMessage({ type: 'success', text: res.data.message });
      fetchClients();
    } catch (err) {
      setMessage({ type: 'error', text: tError(err.response?.data?.detail) || t('clientPwd.errorSendEmail') });
    } finally {
      setSending(p => ({ ...p, [clientId]: null }));
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const emailClients = clients.filter(c => c.email && c.temp_pin && !c.password_sent);
    if (selected.size === emailClients.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(emailClients.map(c => c.id)));
    }
  };

  const sendBulk = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    try {
      const res = await api.post('/clients/send-password-bulk', { client_ids: [...selected] });
      setMessage({ type: 'success', text: t('clientPwd.bulkResult', { sent: res.data.sent, errors: res.data.errors }) });
      setSelected(new Set());
      fetchClients();
    } catch (err) {
      setMessage({ type: 'error', text: tError(err.response?.data?.detail) || t('clientPwd.errorSendBulk') });
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto fade-in space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="client-passwords-title">
            <Users className="w-6 h-6 text-cyan-400" />
            {t('clientPwd.title')}
          </h2>
          <p className="text-sm text-gray-400 mt-1">{t('clientPwd.totalClients', { total })}</p>
        </div>
          {selected.size > 0 && (
            <button onClick={sendBulk} disabled={bulkSending} className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold flex items-center gap-2" data-testid="bulk-send-btn">
              <Send className="w-4 h-4" />
              {bulkSending ? t('clientPwd.sending') : t('clientPwd.sendToClients', { count: selected.size })}
            </button>
          )}
        </div>

        {message && (
          <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`} data-testid="status-message">
            {message.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-auto text-gray-400 hover:text-white">✕</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('clientPwd.searchPlaceholder')}
              className="input-dark pl-10 w-full"
              data-testid="search-clients"
            />
          </div>
          <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }} className="input-dark" data-testid="filter-sent">
            <option value="all">{t('clientPwd.filterAll')}</option>
            <option value="sent">{t('clientPwd.filterSent')}</option>
            <option value="not_sent">{t('clientPwd.filterNotSent')}</option>
          </select>
          <button onClick={fetchClients} className="p-2 rounded-lg bg-white/10 hover:bg-white/20" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-gray-400">
                <th className="p-3 text-left">
                  <input type="checkbox" onChange={selectAll} checked={selected.size > 0} className="accent-blue-500" />
                </th>
                <th className="p-3 text-left">{t('clientPwd.colName')}</th>
                <th className="p-3 text-left">{t('clientPwd.colPhone')}</th>
                <th className="p-3 text-left">{t('clientPwd.colEmail')}</th>
                <th className="p-3 text-center">{t('clientPwd.colPin')}</th>
                <th className="p-3 text-center">{t('clientPwd.colStatus')}</th>
                <th className="p-3 text-center">{t('clientPwd.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">{t('clientPwd.loading')}</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">{t('clientPwd.noClients')}</td></tr>
              ) : clients.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`client-row-${c.id}`}>
                  <td className="p-3">
                    {c.email && c.temp_pin && !c.password_sent && (
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="accent-blue-500" />
                    )}
                  </td>
                  <td className="p-3 font-medium text-white">{c.nom}</td>
                  <td className="p-3 text-gray-400">{c.telephone}</td>
                  <td className="p-3 text-gray-400">{c.email || <span className="text-gray-600 italic">{t('clientPwd.noEmail')}</span>}</td>
                  <td className="p-3 text-center">
                    {c.temp_pin ? (
                      <span className="font-mono text-yellow-300 tracking-widest">{c.temp_pin}</span>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {c.password_sent ? (
                      <span className="text-xs px-2 py-1 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30">{t('clientPwd.statusSent')}</span>
                    ) : c.temp_pin ? (
                      <span className="text-xs px-2 py-1 rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">{t('clientPwd.statusPending')}</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-lg bg-gray-500/20 text-gray-400 border border-gray-500/30">{t('clientPwd.statusNotGenerated')}</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => generatePin(c.id)}
                        disabled={sending[c.id] === 'generating'}
                        className="p-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 transition-colors"
                        title={t('clientPwd.genPinTitle')}
                        data-testid={`gen-pin-${c.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {c.email && c.temp_pin && (
                        <button
                          onClick={() => sendEmail(c.id)}
                          disabled={sending[c.id] === 'sending'}
                          className={`p-2 rounded-lg transition-colors ${c.password_sent ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'}`}
                          title={c.password_sent ? t('clientPwd.resend') : t('clientPwd.sendByEmail')}
                          data-testid={`send-email-${c.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="prev-page">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-400">{t('clientPwd.pageOf', { page, total: totalPages })}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="next-page">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
    </div>
  );
};

const ClientPasswordsPage = () => (
  <Layout>
    <ClientPasswordsContent />
  </Layout>
);

export default ClientPasswordsPage;
